import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SelectField } from "@/components/ui/select";

import {
  ArrowLeft,
  User,
  Building2,
  Shield,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Edit,
  Briefcase,
  FileText,
  Eye,
  EyeOff,
  Loader2,
  Award,
} from "lucide-react";
import { toast } from "sonner";
import { apiService, User as BackendUser } from "@/services/api";
import { getRoleDisplayName, UserStatus, getUserStatusDisplayName, UserRole } from "@/constants/enums";


interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: "worker" | "employer" | "admin";
  status: "created" | "pending_verification" | "verified" | "deleted" | "blocked";
  statusCode: number; // Store the numeric status code
  createdAt: string;
  lastLogin: string;
  phone?: string;
  avatar?: string;

  // Worker specific fields
  workerProfile?: {
    skills: string[];
    hourlyRate: number;
    availability: string[];
    totalJobs: number;
    totalEarnings: number;
    rating: number;
    reviews: number;
    completedShifts: number;
    cancelledShifts: number;
    backgroundCheck: {
      status: "pending" | "passed" | "failed" | "not_required";
      date?: string;
    };
    documents: {
      type: string;
      name: string;
      status: "pending" | "approved" | "rejected";
      uploadedAt: string;
    }[];
    preferences: {
      maxDistance: number;
      preferredCategories: string[];
      minHourlyRate: number;
    };
  };

  // Employer specific fields
  employerProfile?: {
    companyName: string;
    industry: string;
    companySize: string;
    location: string;
    totalJobsPosted: number;
    totalHires: number;
    averageRating: number;
    totalSpent: number;
    subscriptionPlan: string;
    subscriptionStatus: "active" | "expired" | "cancelled";
    paymentMethod?: {
      type: string;
      last4: string;
      expiryDate: string;
    };
    billingAddress: {
      street: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
    };
  };

  // Admin specific fields
  adminProfile?: {
    permissions: string[];
    lastActions: {
      action: string;
      target: string;
      timestamp: string;
      ipAddress: string;
    }[];
    accessLevel: "super_admin" | "admin" | "moderator";
  };
}

export default function UserProfileViewer() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [backendUser, setBackendUser] = useState<BackendUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [showSensitiveInfo, setShowSensitiveInfo] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    fullName: "",
    email: "",
    role: 0,
    userStatus: 0,
  });

  // Fetch user data from API
  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) {
        toast.error("User ID is missing");
        navigate("/users");
        return;
      }

      try {
        setIsLoading(true);
        const response = await apiService.getUserById(userId);
        
        if (response.type === 0 && response.data) {
          const user = response.data;
          
          // Store the complete backend user data
          setBackendUser(user);
          
          // Split fullName into firstName and lastName
          const nameParts = user.fullName.split(" ");
          const firstName = nameParts[0] || "";
          const lastName = nameParts.slice(1).join(" ") || "";
          
        // Map status code to string
        let statusString: "created" | "pending_verification" | "verified" | "deleted" | "blocked";
        switch (user.userStatus) {
          case UserStatus.CREATED:
            statusString = "created";
            break;
          case UserStatus.PENDING_VERIFICATION:
            statusString = "pending_verification";
            break;
          case UserStatus.VERIFIED:
            statusString = "verified";
            break;
          case UserStatus.DELETED:
            statusString = "deleted";
            break;
          case UserStatus.BLOCKED:
            statusString = "blocked";
            break;
          default:
            statusString = "created";
        }
        
        // Map backend user data to component's expected format
      setUserProfile({
          id: user._id,
          email: user.email,
          firstName,
          lastName,
          role: getRoleDisplayName(user.role).toLowerCase() as "worker" | "employer" | "admin",
          status: statusString,
          statusCode: user.userStatus,
          createdAt: user.createdAt,
          lastLogin: user.updatedAt, // Using updatedAt as lastLogin proxy
          avatar: user.profilePicture?.fileUrl,
          phone: undefined, // Not available in basic user data
        });
        } else {
          toast.error(response.message || "Failed to load user data");
          navigate("/users");
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error("Failed to load user profile");
        navigate("/users");
      } finally {
      setIsLoading(false);
      }
    };

    void fetchUserData();
  }, [userId, navigate]);

  const handleStatusChange = async (newStatus: UserProfile["status"]) => {
    if (!userProfile || !userId) return;

    // Map status string to status code
    let statusCode: number;
    switch (newStatus) {
      case "verified":
        statusCode = UserStatus.VERIFIED;
        break;
      case "blocked":
        statusCode = UserStatus.BLOCKED;
        break;
      case "pending_verification":
        statusCode = UserStatus.PENDING_VERIFICATION;
        break;
      case "created":
        statusCode = UserStatus.CREATED;
        break;
      default:
        statusCode = UserStatus.CREATED;
    }

    try {
      const response = await apiService.updateUser(userId, {
        userStatus: statusCode,
      });

      if (response.type === 0) {
        setUserProfile({ ...userProfile, status: newStatus, statusCode });
        toast.success(`User status updated to ${getUserStatusDisplayName(statusCode)}`, {
          description: "The user's account status has been modified successfully",
        });
      } else {
        toast.error(response.message || "Failed to update user status");
      }
    } catch (error) {
      console.error("Error updating user status:", error);
      toast.error("Failed to update user status");
    }
  };

  const handleSuspendUser = () => {
    handleStatusChange("blocked");
  };

  const handleActivateUser = () => {
    handleStatusChange("verified");
  };

  const handleVerifyUser = () => {
    handleStatusChange("verified");
  };

  const handleEditProfile = () => {
    if (!userProfile) return;
    
    // Convert role string back to numeric enum
    let roleNumber = UserRole.NONE;
    switch (userProfile.role) {
      case "admin":
        roleNumber = UserRole.ADMIN;
        break;
      case "employer":
        roleNumber = UserRole.ESTABLISHMENT;
        break;
      case "worker":
        roleNumber = UserRole.STAFF;
        break;
    }
    
    setEditFormData({
      fullName: `${userProfile.firstName} ${userProfile.lastName}`,
      email: userProfile.email,
      role: roleNumber,
      userStatus: userProfile.statusCode,
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!userProfile) return;
    
    try {
      const response = await apiService.updateUser(userProfile.id, editFormData);
      if (response.type === 0) {
        toast.success("User updated successfully");
        setIsEditDialogOpen(false);
        
        // Refresh the user data
        const updatedResponse = await apiService.getUserById(userProfile.id);
        if (updatedResponse.type === 0 && updatedResponse.data) {
          const user = updatedResponse.data;
          const nameParts = user.fullName.split(" ");
          const firstName = nameParts[0] || "";
          const lastName = nameParts.slice(1).join(" ") || "";
          
          let statusString: "created" | "pending_verification" | "verified" | "deleted" | "blocked";
          switch (user.userStatus) {
            case UserStatus.CREATED:
              statusString = "created";
              break;
            case UserStatus.PENDING_VERIFICATION:
              statusString = "pending_verification";
              break;
            case UserStatus.VERIFIED:
              statusString = "verified";
              break;
            case UserStatus.DELETED:
              statusString = "deleted";
              break;
            case UserStatus.BLOCKED:
              statusString = "blocked";
              break;
            default:
              statusString = "created";
          }
          
          setUserProfile({
            id: user._id,
            email: user.email,
            firstName,
            lastName,
            role: getRoleDisplayName(user.role).toLowerCase() as "worker" | "employer" | "admin",
            status: statusString,
            statusCode: user.userStatus,
            createdAt: user.createdAt,
            lastLogin: user.updatedAt,
            avatar: user.profilePicture?.fileUrl,
            phone: undefined,
          });
        }
      } else {
        toast.error(response.message || "Failed to update user");
      }
    } catch (error) {
      toast.error("Failed to update user");
      console.error("Error updating user:", error);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      created: { variant: "secondary" as const, icon: Clock, text: "Created" },
      pending_verification: { variant: "secondary" as const, icon: Clock, text: "Pending Verification" },
      verified: { variant: "default" as const, icon: CheckCircle, text: "Verified" },
      deleted: { variant: "destructive" as const, icon: XCircle, text: "Deleted" },
      blocked: { variant: "destructive" as const, icon: XCircle, text: "Blocked" },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.created;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center space-x-1">
        <Icon className="w-3 h-3" />
        <span>{config.text}</span>
      </Badge>
    );
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case "worker":
        return <Briefcase className="w-5 h-5" />;
      case "employer":
        return <Building2 className="w-5 h-5" />;
      case "admin":
        return <Shield className="w-5 h-5" />;
      default:
        return <User className="w-5 h-5" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case "worker":
        return "bg-blue-100 text-blue-800";
      case "employer":
        return "bg-green-100 text-green-800";
      case "admin":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading user profile...</p>
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">User Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The requested user profile could not be found.
          </p>
          <Button
            onClick={() => {
              navigate(-1);
            }}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => {
              navigate(-1);
            }}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">User Profile</h1>
            <p className="text-muted-foreground">
              Managing profile for {userProfile.firstName} {userProfile.lastName}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={() => {
              setShowSensitiveInfo(!showSensitiveInfo);
            }}
          >
            {showSensitiveInfo ? (
              <EyeOff className="w-4 h-4 mr-2" />
            ) : (
              <Eye className="w-4 h-4 mr-2" />
            )}
            {showSensitiveInfo ? "Hide" : "Show"} Sensitive Info
          </Button>

          {userProfile.status === "verified" && (
            <Button variant="outline" onClick={handleSuspendUser}>
              Block User
            </Button>
          )}

          {userProfile.status === "blocked" && (
            <Button variant="outline" onClick={handleActivateUser}>
              Unblock User
            </Button>
          )}

          {userProfile.status === "pending_verification" && (
            <Button onClick={handleVerifyUser}>Verify User</Button>
          )}

          <Button onClick={handleEditProfile}>
            <Edit className="w-4 h-4 mr-2" />
            Edit Profile
          </Button>
        </div>
      </div>

      {/* Profile Overview Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <Avatar className="w-20 h-20">
                <AvatarImage src={userProfile.avatar} alt={`${userProfile.firstName} ${userProfile.lastName}`} />
                <AvatarFallback>
                  {userProfile.firstName[0]}{userProfile.lastName[0]}
                </AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="text-2xl">
                  {userProfile.firstName} {userProfile.lastName}
                </CardTitle>
                <CardDescription className="text-lg">{userProfile.email}</CardDescription>
                <div className="flex items-center space-x-2 mt-2">
                  {getStatusBadge(userProfile.status)}
                  <Badge className={getRoleColor(userProfile.role)}>
                    {getRoleIcon(userProfile.role)}
                    <span className="ml-1 capitalize">{userProfile.role}</span>
                  </Badge>
                </div>
              </div>
            </div>

            <div className="text-right text-sm text-muted-foreground">
              <div>Member since {new Date(userProfile.createdAt).toLocaleDateString()}</div>
              <div>Last login {new Date(userProfile.lastLogin).toLocaleDateString()}</div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {/* Basic Info */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center space-x-2">
                  <User className="w-5 h-5" />
                  <span>Basic Information</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Full Name:</span>
                  <span className="font-medium">
                    {userProfile.firstName} {userProfile.lastName}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email:</span>
                  <span className="font-medium">{userProfile.email}</span>
                </div>
                {showSensitiveInfo && userProfile.phone && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Phone:</span>
                    <span className="font-medium">{userProfile.phone}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  {getStatusBadge(userProfile.status)}
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Role:</span>
                  <Badge className={getRoleColor(userProfile.role)}>
                    {getRoleIcon(userProfile.role)}
                    <span className="ml-1 capitalize">{userProfile.role}</span>
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Staff Profile Info */}
            {backendUser?.staffProfile && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center space-x-2">
                    <Briefcase className="w-5 h-5" />
                    <span>Staff Profile</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {backendUser.staffProfile.hourlyRate && (
                  <div className="flex justify-between">
                      <span className="text-muted-foreground">Hourly Rate:</span>
                      <span className="font-medium">${backendUser.staffProfile.hourlyRate}/hr</span>
                  </div>
                  )}
                  {backendUser.staffProfile.industry && (
                  <div className="flex justify-between">
                      <span className="text-muted-foreground">Industry:</span>
                      <span className="font-medium">{backendUser.staffProfile.industry}</span>
                  </div>
                  )}
                  {backendUser.staffProfile.designation && (
                  <div className="flex justify-between">
                      <span className="text-muted-foreground">Designation:</span>
                      <span className="font-medium">{backendUser.staffProfile.designation}</span>
                    </div>
                  )}
                  {backendUser.staffProfile.experience && (
                  <div className="flex justify-between">
                      <span className="text-muted-foreground">Experience:</span>
                      <span className="font-medium">{backendUser.staffProfile.experience}</span>
                  </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Available to Hire:</span>
                    <Badge variant={backendUser.staffProfile.availableToHire ? "default" : "secondary"}>
                      {backendUser.staffProfile.availableToHire ? "Yes" : "No"}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Employer Profile Stats */}
            {userProfile.employerProfile && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center space-x-2">
                    <Building2 className="w-5 h-5" />
                    <span>Company Statistics</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Company:</span>
                    <span className="font-medium">{userProfile.employerProfile.companyName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Industry:</span>
                    <span className="font-medium">{userProfile.employerProfile.industry}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Jobs Posted:</span>
                    <span className="font-medium">
                      {userProfile.employerProfile.totalJobsPosted}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Hires:</span>
                    <span className="font-medium">{userProfile.employerProfile.totalHires}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Spent:</span>
                    <span className="font-medium">
                      ${userProfile.employerProfile.totalSpent.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Plan:</span>
                    <Badge
                      variant={
                        userProfile.employerProfile.subscriptionStatus === "active"
                          ? "default"
                          : "secondary"
                      }
                    >
                      {userProfile.employerProfile.subscriptionPlan}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Establishments List */}
          {backendUser?.establishments && backendUser.establishments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Building2 className="w-5 h-5" />
                  <span>Establishments ({backendUser.establishments.length})</span>
                </CardTitle>
                <CardDescription>
                  Establishments owned or managed by this user
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  {backendUser.establishments.map((establishment) => {
                    return(
                    <Card
                      key={establishment._id}
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => {
                        navigate(`/establishments/${establishment._id}`);
                      }}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center space-x-3">
                          <Avatar className="h-12 w-12">
                            <AvatarImage
                              src={establishment.establishmentLogo?.fileUrl}
                              alt={establishment.establishmentName}
                            />
                            <AvatarFallback>
                              {establishment.establishmentName
                                .split(" ")
                                .map((n) => n[0])
                                .join("")}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="font-medium">{establishment.establishmentName}</div>
                            <div className="text-sm text-muted-foreground">
                              {establishment.serviceType}
                            </div>
                            {establishment.address && typeof establishment.address === "object" && (
                              <div className="text-xs text-muted-foreground mt-1">
                                {(establishment.address as { city?: string; state?: string }).city},{" "}
                                {(establishment.address as { city?: string; state?: string }).state}
                              </div>
                            )}
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/establishments/${establishment._id}`);
                            }}
                          >
                            View
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )})}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Details Tab */}
        <TabsContent value="details" className="space-y-4">
          {backendUser?.staffProfile && (
            <Card>
              <CardHeader>
                <CardTitle>Staff Profile Details</CardTitle>
                <CardDescription>
                  Complete staff member information and qualifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium mb-3">Professional Information</h4>
                    <div className="space-y-2">
                      {backendUser.staffProfile.industry && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Industry:</span>
                          <span className="font-medium">{backendUser.staffProfile.industry}</span>
                        </div>
                      )}
                      {backendUser.staffProfile.designation && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Designation:</span>
                          <span className="font-medium">{backendUser.staffProfile.designation}</span>
                        </div>
                      )}
                      {backendUser.staffProfile.experience && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Experience:</span>
                          <span className="font-medium">{backendUser.staffProfile.experience}</span>
                        </div>
                      )}
                      {backendUser.staffProfile.hourlyRate && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Hourly Rate:</span>
                          <span className="font-medium">${backendUser.staffProfile.hourlyRate}/hr</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-3">Personal Details</h4>
                    <div className="space-y-2">
                      {backendUser.staffProfile.gender && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Gender:</span>
                          <span className="font-medium">{backendUser.staffProfile.gender}</span>
                        </div>
                      )}
                      {backendUser.staffProfile.dateOfBirth && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Date of Birth:</span>
                          <span className="font-medium">
                            {new Date(backendUser.staffProfile.dateOfBirth).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Available to Hire:</span>
                        <Badge variant={backendUser.staffProfile.availableToHire ? "default" : "secondary"}>
                          {backendUser.staffProfile.availableToHire ? "Yes" : "No"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Skills */}
                {backendUser.staffProfile.skills && backendUser.staffProfile.skills.length > 0 && (
                <div>
                    <h4 className="font-medium mb-3">Skills</h4>
                  <div className="flex flex-wrap gap-2">
                      {backendUser.staffProfile.skills.map((skill, index) => (
                      <Badge key={index} variant="secondary">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
                )}

                {/* Bio */}
                {backendUser.staffProfile.bio && (
                <div>
                    <h4 className="font-medium mb-3">Bio</h4>
                    <div className="p-3 bg-muted rounded-lg">
                      <p>{backendUser.staffProfile.bio}</p>
                  </div>
                </div>
                )}

                {/* Social Links */}
                {(backendUser.staffProfile.website ||
                  backendUser.staffProfile.linkedin ||
                  backendUser.staffProfile.instagram) && (
                <div>
                    <h4 className="font-medium mb-3">Social Links</h4>
                    <div className="space-y-2">
                      {backendUser.staffProfile.website && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Website:</span>
                          <a
                            href={backendUser.staffProfile.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {backendUser.staffProfile.website}
                          </a>
                      </div>
                      )}
                      {backendUser.staffProfile.linkedin && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">LinkedIn:</span>
                          <a
                            href={backendUser.staffProfile.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {backendUser.staffProfile.linkedin}
                          </a>
                    </div>
                      )}
                      {backendUser.staffProfile.instagram && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Instagram:</span>
                          <a
                            href={backendUser.staffProfile.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {backendUser.staffProfile.instagram}
                          </a>
                      </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Address */}
                {backendUser.staffProfile.address && (
                  <div>
                    <h4 className="font-medium mb-3">Address</h4>
                    <div className="p-3 bg-muted rounded-lg">
                      <div>{backendUser.staffProfile.address.street}</div>
                      <div>
                        {backendUser.staffProfile.address.city}, {backendUser.staffProfile.address.state}{" "}
                        {backendUser.staffProfile.address.postalCode}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {userProfile.employerProfile && (
            <Card>
              <CardHeader>
                <CardTitle>Company Details</CardTitle>
                <CardDescription>Business information and billing details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Company Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium mb-3">Company Information</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Company Name:</span>
                        <span className="font-medium">
                          {userProfile.employerProfile.companyName}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Industry:</span>
                        <span className="font-medium">{userProfile.employerProfile.industry}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Company Size:</span>
                        <span className="font-medium">
                          {userProfile.employerProfile.companySize}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Location:</span>
                        <span className="font-medium">{userProfile.employerProfile.location}</span>
                      </div>
                    </div>
                  </div>

                  {showSensitiveInfo && (
                    <div>
                      <h4 className="font-medium mb-3">Billing Information</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Subscription Plan:</span>
                          <span className="font-medium">
                            {userProfile.employerProfile.subscriptionPlan}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Status:</span>
                          <Badge
                            variant={
                              userProfile.employerProfile.subscriptionStatus === "active"
                                ? "default"
                                : "secondary"
                            }
                          >
                            {userProfile.employerProfile.subscriptionStatus}
                          </Badge>
                        </div>
                        {userProfile.employerProfile.paymentMethod && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Payment Method:</span>
                            <span className="font-medium">
                              {userProfile.employerProfile.paymentMethod.type} ••••{" "}
                              {userProfile.employerProfile.paymentMethod.last4}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Billing Address */}
                {showSensitiveInfo && userProfile.employerProfile.billingAddress && (
                  <div>
                    <h4 className="font-medium mb-3">Billing Address</h4>
                    <div className="p-3 bg-muted rounded-lg">
                      <div>{userProfile.employerProfile.billingAddress.street}</div>
                      <div>
                        {userProfile.employerProfile.billingAddress.city},{" "}
                        {userProfile.employerProfile.billingAddress.state}{" "}
                        {userProfile.employerProfile.billingAddress.zipCode}
                      </div>
                      <div>{userProfile.employerProfile.billingAddress.country}</div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>User's recent actions and job history</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center space-x-3 p-3 bg-muted rounded-lg">
                  <Clock className="w-5 h-5 text-muted-foreground" />
                  <div className="flex-1">
                    <div className="font-medium">Last Login</div>
                    <div className="text-sm text-muted-foreground">
                      {new Date(userProfile.lastLogin).toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-muted rounded-lg">
                  <Calendar className="w-5 h-5 text-muted-foreground" />
                  <div className="flex-1">
                    <div className="font-medium">Account Created</div>
                    <div className="text-sm text-muted-foreground">
                      {new Date(userProfile.createdAt).toLocaleString()}
                    </div>
                  </div>
                </div>

                {userProfile.workerProfile && (
                  <div className="flex items-center space-x-3 p-3 bg-muted rounded-lg">
                    <Briefcase className="w-5 h-5 text-muted-foreground" />
                    <div className="flex-1">
                      <div className="font-medium">Total Jobs Completed</div>
                      <div className="text-sm text-muted-foreground">
                        {userProfile.workerProfile.completedShifts} completed,{" "}
                        {userProfile.workerProfile.cancelledShifts} cancelled
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          {backendUser?.staffProfile && (
            <>
              {/* Resume */}
              {backendUser.staffProfile.resume && (
            <Card>
              <CardHeader>
                    <CardTitle>Resume</CardTitle>
                    <CardDescription>Staff member's uploaded resume</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center space-x-3">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <div className="font-medium">
                            {backendUser.staffProfile.resume.name || "Resume"}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {backendUser.staffProfile.resume.fileType} •{" "}
                            {(backendUser.staffProfile.resume.size / 1024).toFixed(2)} KB
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(backendUser.staffProfile?.resume?.fileUrl, "_blank")}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Certifications */}
              {backendUser.staffProfile.certifications &&
                backendUser.staffProfile.certifications.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Certifications</CardTitle>
                    <CardDescription>
                      Professional certifications and qualifications
                    </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                      {backendUser.staffProfile.certifications.map((cert, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                            <Award className="w-5 h-5 text-muted-foreground" />
                        <div>
                              <div className="font-medium">{cert.name}</div>
                              <div className="text-sm text-muted-foreground">
                                {cert.fileType} • {(cert.size / 1024).toFixed(2)} KB
                          </div>
                        </div>
                      </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(cert.fileUrl, "_blank")}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View
                        </Button>
                    </div>
                  ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {!backendUser.staffProfile.resume &&
                (!backendUser.staffProfile.certifications ||
                  backendUser.staffProfile.certifications.length === 0) && (
                <Card>
                  <CardContent className="py-8">
                    <div className="text-center text-muted-foreground">
                      <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No documents uploaded yet</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {!backendUser?.staffProfile && (
            <Card>
              <CardContent className="py-8">
                <div className="text-center text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No documents available for this user</p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Profile Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User Profile</DialogTitle>
            <DialogDescription>
              Update user information. Changes will be saved immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-fullName">Full Name</Label>
              <Input
                id="edit-fullName"
                value={editFormData.fullName}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, fullName: e.target.value })
                }
                placeholder="Enter full name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editFormData.email}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, email: e.target.value })
                }
                placeholder="Enter email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role">Role</Label>
              <SelectField
                value={String(editFormData.role)}
                onValueChange={(value: string) =>
                  setEditFormData({ ...editFormData, role: parseInt(value, 10) })
                }
                placeholder="Select role"
                options={[
                  { label: "Admin", value: String(UserRole.ADMIN) },
                  { label: "Manager", value: String(UserRole.MANAGER) },
                  { label: "Staff", value: String(UserRole.STAFF) },
                  { label: "Establishment", value: String(UserRole.ESTABLISHMENT) },
                ]}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-status">Status</Label>
              <SelectField
                value={String(editFormData.userStatus)}
                onValueChange={(value: string) =>
                  setEditFormData({ ...editFormData, userStatus: parseInt(value, 10) })
                }
                placeholder="Select status"
                options={[
                  { label: "Created", value: String(UserStatus.CREATED) },
                  { label: "Pending Verification", value: String(UserStatus.PENDING_VERIFICATION) },
                  { label: "Verified", value: String(UserStatus.VERIFIED) },
                  { label: "Blocked", value: String(UserStatus.BLOCKED) },
                ]}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsEditDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
